/**
 * Code-based routes, not file-based: four surfaces fit on one screen, and a
 * route's search-param schema is declared next to the route that owns it rather
 * than inferred from a filename.
 */
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router'
import { AppShell } from './layout/AppShell'
import { ChatPage } from '@/routes/ChatPage'
import { AccountPage } from '@/features/account/AccountPage'
import { ExternalSharePage } from '@/routes/ExternalSharePage'
import { PortalPage } from '@/features/portal/PortalPage'
import { MeetingsPage } from '@/routes/MeetingsPage'
import { SettingsPage } from '@/routes/SettingsPage'
import { SpacesPage } from '@/routes/SpacesPage'
import { SETTINGS_SECTIONS, type SettingsSection } from '@/features/settings/sections'
import { TemplatesPage } from '@/routes/TemplatesPage'

const rootRoute = createRootRoute()

/**
 * The shell is a LAYOUT route, not the root.
 *
 * Everything the product is used through hangs off it and gets the sidebar, the
 * top bar and the command palette. The external share page does not: it is
 * opened from a link in an email to answer one question about one meeting, and
 * a chrome offering chat, templates and spaces would be offering everything
 * except the thing the reader came for. A shell on the root route cannot be
 * opted out of, so it is one level down.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AppShell,
})

/**
 * Chat is the product's front door, so `/` is it rather than a dashboard that
 * would only ever be passed through.
 */
const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/chat' })
  },
})

const chatRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chat',
  component: function NewChat() {
    return <ChatPage threadId={null} />
  },
})

const chatDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chat/$threadId',
  component: function ChatDetail() {
    const { threadId } = chatDetailRoute.useParams()
    return <ChatPage threadId={threadId} />
  },
})

const meetingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/meetings',
  component: MeetingsPage,
})

const templatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/templates',
  component: TemplatesPage,
})

const spacesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/spaces',
  component: SpacesPage,
})

/**
 * Not in the sidebar. Settings is administered rarely and by few people, and a
 * fifth permanent nav row for it would put it in the same weight class as the
 * three surfaces the product is used through — it is reached from the account
 * menu, where the organisation is already named.
 */
/**
 * Your own settings, beside Settings rather than inside it.
 *
 * Not in the sidebar for the same reason Settings is not: it is visited rarely
 * and reached from the account menu, where the person is already named.
 */
const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/account',
  component: AccountPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  /**
   * The open section is in the URL, so "the deletion policy is under Access"
   * can be sent as a link rather than as directions.
   *
   * Optional rather than defaulted to `recording`: a required param would make
   * every plain link to `/settings` a type error and put `?tab=recording` in
   * the address bar of a page nobody navigated within. An unknown value is
   * dropped rather than rejected — a stale link should still land somewhere.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsSection } => {
    const tab = search.tab as SettingsSection
    return SETTINGS_SECTIONS.includes(tab) ? { tab } : {}
  },
  component: SettingsPage,
})

/**
 * The landing page for the recap email's share button, outside the shell.
 *
 * The token is a path segment rather than a query string because it is the
 * page's subject, not an option on it — there is no version of this route that
 * means anything without one.
 */
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: function ExternalShare() {
    const { token } = shareRoute.useParams()
    return <ExternalSharePage token={token} />
  },
})

/**
 * Paths the old app owned, which are already out in the world.
 *
 * The service builds email links from `FRONTEND_URL`, so every share
 * notification and recap ever sent points at this domain with the old app's
 * routes. Those inboxes cannot be rewritten, so the addresses have to keep
 * resolving — for as long as the mail exists, which is indefinitely.
 *
 * Redirects rather than duplicate pages: there is one meetings surface and one
 * share page, and a second route rendering the same thing is a second thing to
 * keep correct.
 */
const legacyShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share-external',
  // The token moved from a query string to a path segment; a link without one
  // is a truncated paste, and the share page says that better than a 404 here.
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/share/$token', params: { token: search.token ?? '' } })
  },
})

const legacyDashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/dashboard/$',
  beforeLoad: ({ params }) => {
    // `/dashboard/meetings?meeting=<id>` is the share notification's link. The
    // meeting id is dropped rather than honoured: opening one meeting from a
    // link needs a route that can address it, which the list does not have yet.
    const rest = (params as { _splat?: string })._splat ?? ''
    const target = rest.startsWith('notes')
      ? '/templates'
      : rest.startsWith('spaces')
        ? '/spaces'
        : rest.startsWith('settings')
          ? '/settings'
          : '/meetings'
    throw redirect({ to: target })
  },
})

/**
 * The external portal, outside the shell.
 *
 * Three addresses because the emails already use three: the overview, the
 * sign-in landing (which carries `?token=`), and a direct link to one meeting.
 * They all render the same component — the portal is three states of one short
 * task, and splitting it into three pages would mean three ways to be signed
 * out.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  component: function Portal() {
    return <PortalPage />
  },
})

const portalSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/sign-in',
  component: function PortalSignIn() {
    return <PortalPage />
  },
})

const portalMeetingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/meetings/$meetingId',
  component: function PortalMeeting() {
    const { meetingId } = portalMeetingRoute.useParams()
    return <PortalPage meetingId={meetingId} />
  },
})

const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    indexRoute,
    chatRoute,
    chatDetailRoute,
    meetingsRoute,
    templatesRoute,
    spacesRoute,
    settingsRoute,
    accountRoute,
    legacyDashboardRoute,
  ]),
  shareRoute,
  legacyShareRoute,
  portalRoute,
  portalSignInRoute,
  portalMeetingRoute,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
